/**
 * In-house CSV Parser - No external APIs needed
 * Parses CSV schedule data into structured tickets with robust matching
 */

interface JobCatalogEntry {
  label: string;
  category_key: string;
  sub_menu: string;
  minutes_default: number;
  skill_level: string;
}

interface Technician {
  name: string;
}

interface ParsedJob {
  category: string;
  submenu: string;
  name: string;
  minutes: number;
  customHours?: number;
  notes?: string;
  bay: string;
  skill: number;
}

interface ParsedTicket {
  ticketNumber: string;
  year?: number;
  make?: string;
  model?: string;
  engineSize?: string;
  customerName: string;
  customerPhone?: string;
  jobs: ParsedJob[];
  assignedTechnician?: string;
  startTime?: string;
  appointmentDate: string;
  status: string;
  isComeback?: boolean;
  vin?: string;
  /** OD-40: what this row lacks for placement (ticket number, customer name, jobs); absent when complete. */
  missingInfo?: string[];
}

// Normalize text for fuzzy matching
const normalizeText = (s: string): string => 
  (s || '').toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Calculate Levenshtein distance for fuzzy matching
const levenshtein = (a: string, b: string): number => {
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  
  const dp = new Array(n + 1).fill(0);
  for (let j = 0; j <= n; j++) dp[j] = j;
  
  for (let i = 1; i <= m; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= n; j++) {
      const temp = dp[j];
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[j] = Math.min(dp[j] + 1, dp[j - 1] + 1, prev + cost);
      prev = temp;
    }
  }
  return dp[n];
};

// Calculate token overlap for fuzzy matching
const tokenOverlap = (na: string, nb: string): number => {
  const ta = new Set(na.split(' ').filter(Boolean));
  const tb = new Set(nb.split(' ').filter(Boolean));
  if (ta.size === 0 || tb.size === 0) return 0;
  
  let inter = 0;
  ta.forEach(t => { if (tb.has(t)) inter++; });
  return inter / Math.min(ta.size, tb.size);
};

// Normalize make names to standard formats
const normalizeMake = (make: string): string => {
  const makeUpper = make.toUpperCase();
  const makeMap: Record<string, string> = {
    'CHEVY': 'CHEVROLET',
    'CADDY': 'CADILLAC',
    'BENZ': 'MERCEDES-BENZ',
    'MB': 'MERCEDES-BENZ',
    'VW': 'VOLKSWAGEN',
    'BEEMER': 'BMW',
    'BIMMER': 'BMW',
  };
  return makeMap[makeUpper] || makeUpper;
};

// Parse CSV text into array of objects
const parseCSV = (csvText: string): Record<string, string>[] => {
  const lines = csvText.split(/\r?\n/).filter(l => l.trim().length > 0);
  if (lines.length < 2) return [];
  
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
  const rows: Record<string, string>[] = [];
  
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',');
    const row: Record<string, string> = {};
    headers.forEach((header, idx) => {
      row[header] = (values[idx] || '').trim();
    });
    rows.push(row);
  }
  
  return rows;
};

// Find best matching job from catalog
const findBestJobMatch = (
  jobName: string, 
  catalog: Array<JobCatalogEntry & { _norm: string }>
): (JobCatalogEntry & { _norm: string }) | null => {
  const jobNorm = normalizeText(jobName);
  if (!jobNorm) return null;
  
  let best: (JobCatalogEntry & { _norm: string }) | null = null;
  let bestScore = 0;
  
  for (const c of catalog) {
    const labelNorm = c._norm;
    let score = 0;
    
    // Exact match
    if (jobNorm === labelNorm) {
      score = 1;
    } else {
      // Substring match
      const inc = (jobNorm.includes(labelNorm) || labelNorm.includes(jobNorm)) ? 0.9 : 0;
      // Levenshtein distance
      const lev = 1 - (levenshtein(jobNorm, labelNorm) / Math.max(jobNorm.length, labelNorm.length));
      // Token overlap
      const tok = tokenOverlap(jobNorm, labelNorm);
      score = Math.max(inc, lev, tok);
    }
    
    if (score > bestScore) {
      bestScore = score;
      best = c;
    }
  }
  
  // Accept match if reasonably similar (threshold: 0.48)
  return (best && bestScore >= 0.48) ? best : null;
};

// Extract jobs from row (handles job1, job2, job3 columns)
const extractJobs = (
  row: Record<string, string>,
  catalog: Array<JobCatalogEntry & { _norm: string }>
): ParsedJob[] => {
  const jobs: ParsedJob[] = [];
  const skillLevelMap: Record<string, number> = {
    'basic': 1,
    'intermediate': 2,
    'advanced': 3,
    'expert': 4,
  };
  
  // Look for job columns (job1, job2, job3, etc. OR job, service1, service2, etc.)
  const jobKeys = Object.keys(row).filter(k => 
    k.match(/^(job|service)\d*$/) && row[k].trim().length > 0
  );
  
  for (const jobKey of jobKeys) {
    const jobName = row[jobKey];
    if (!jobName || jobName.trim().length === 0) continue;
    
    // Find matching hours column (job1_hours, hours1, etc.)
    const jobNum = jobKey.match(/\d+$/)?.[0] || '';
    const hourKeys = [
      `${jobKey}_hours`,
      `hours${jobNum}`,
      `${jobKey}_hrs`,
      `hrs${jobNum}`,
      'hours',
      'hrs'
    ];
    
    let hours: number | undefined;
    for (const hk of hourKeys) {
      if (row[hk] && !isNaN(parseFloat(row[hk]))) {
        hours = parseFloat(row[hk]);
        break;
      }
    }
    
    // Match job to catalog
    const match = findBestJobMatch(jobName, catalog);
    if (match) {
      jobs.push({
        category: match.category_key,
        submenu: match.sub_menu,
        name: match.label,
        minutes: hours ? Math.round(hours * 60) : match.minutes_default,
        customHours: hours,
        bay: 'Flat',
        skill: skillLevelMap[(match.skill_level || '').toLowerCase()] || 2,
      });
    } else {
      // Fallback: create a custom job so we never drop a ticket
      const fallbackMinutes = Math.max(6, Math.round(((hours ? hours * 60 : 60)) / 6) * 6);
      jobs.push({
        category: 'maintenance',
        submenu: 'Imported CSV',
        name: jobName,
        minutes: fallbackMinutes,
        customHours: hours,
        notes: 'Imported from CSV (no catalog match)',
        bay: 'Flat',
        skill: 2,
      });
      console.warn('Unmatched job from CSV, using custom job:', jobName);
    }
  }
  
  return jobs;
};

// Get value from row with column name variations
const getRowValue = (row: Record<string, string>, variations: string[]): string | undefined => {
  for (const v of variations) {
    if (row[v] && row[v].trim().length > 0) {
      return row[v].trim();
    }
  }
  return undefined;
};

// Parse entire CSV into tickets.
// `fallbackDateLabel` (YYYY-MM-DD, shop-local) is used when a row omits an
// appointment date. Callers MUST supply it from shop-local "today" (via
// getTodayInShopTz) — the prior implicit `new Date().toISOString().slice(0,10)`
// fallback fell back to the browser's UTC date, which can silently shift the
// scheduled day for any browser not in UTC.
export const parseScheduleCSV = async (
  csvText: string,
  jobCatalog: JobCatalogEntry[],
  technicians: Technician[],
  fallbackDateLabel?: string
): Promise<ParsedTicket[]> => {
  const rows = parseCSV(csvText);
  const catalogNormalized = jobCatalog.map(c => ({
    ...c,
    _norm: normalizeText(c.label)
  }));
  
  const tickets: ParsedTicket[] = [];
  
  for (const row of rows) {
    // Extract ticket number (VIN, RO, ticket_id, work_order, etc.)
    const ticketNumber = getRowValue(row, [
      'ticketnumber', 'ticket_number', 'ticket', 'vin', 'ro', 'ro_number', 
      'work_order', 'workorder', 'id'
    ]);
    
    // OD-40 (founder, 2026-10-15): a row missing what placement needs is REPORTED with what is missing (so the
    // person can correct the CSV or leave it out) — never silently dropped, and nothing is invented to fill it.
    const missingInfo: string[] = [];
    if (!ticketNumber) missingInfo.push('ticket number');
    
    // Extract customer name
    const customerName = getRowValue(row, [
      'customername', 'customer_name', 'customer', 'name', 'client'
    ]);
    
    if (!customerName) missingInfo.push('customer name');
    
    // Extract vehicle data
    const yearStr = getRowValue(row, ['year', 'yr', 'model_year']);
    const year = yearStr ? parseInt(yearStr) : undefined;
    const make = getRowValue(row, ['make', 'mfg', 'manufacturer']);
    const model = getRowValue(row, ['model', 'mdl']);
    const engineSize = getRowValue(row, ['enginesize', 'engine_size', 'engine', 'displacement']);
    
    // Extract customer phone
    let customerPhone = getRowValue(row, ['customerphone', 'customer_phone', 'phone', 'phone_number', 'tel', 'contact']);
    if (customerPhone) {
      const phoneDigits = customerPhone.replace(/\D/g, '');
      if (phoneDigits.length === 10) {
        customerPhone = `${phoneDigits.slice(0, 3)}-${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6)}`;
      }
    }
    
    // Extract jobs. OD-40: a row with no recognisable work is flagged, not given an invented 60-minute job —
    // OptiPlace would otherwise schedule work nobody asked for.
    const jobs = extractJobs(row, catalogNormalized);
    if (jobs.length === 0) missingInfo.push('jobs');
    
    // Extract optional fields
    const assignedTechnician = getRowValue(row, ['assignedtechnician', 'assigned_technician', 'tech', 'technician', 'assigned_to']);
    const startTime = getRowValue(row, ['starttime', 'start_time', 'start', 'time']);
    const appointmentDateStr = getRowValue(row, ['appointmentdate', 'appointment_date', 'date', 'appt_date']);
    const status = getRowValue(row, ['status', 'state']);
    const vin = getRowValue(row, ['vin', 'vehicle_vin', 'chassis']);
    const comebackStr = getRowValue(row, ['comeback', 'is_comeback', 'return']);
    const isComeback = comebackStr ? comebackStr.toLowerCase() === 'true' || comebackStr === '1' || comebackStr.toLowerCase() === 'yes' : false;
    
    // Build ticket
    const ticket: ParsedTicket = {
      ticketNumber,
      customerName,
      jobs,
      // Prefer CSV-provided date; else shop-local "today" from caller.
      // Browser-local format() used only when caller did NOT supply a
      // fallbackDateLabel — browser-local is still better than UTC slice
      // (toISOString would shift day in the local evening for non-UTC
      // shops). Callers that can resolve shop TZ MUST pass fallbackDateLabel.
      appointmentDate: appointmentDateStr || fallbackDateLabel || (() => {
        console.warn('[csvParser] No appointmentDate on row and no fallbackDateLabel from caller; using browser-local date. Wire shop TZ through for shop-local truth.');
        const now = new Date();
        const y = now.getFullYear();
        const m = String(now.getMonth() + 1).padStart(2, '0');
        const d = String(now.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      })(),
      status: status || 'Active',
      isComeback,
      ...(missingInfo.length > 0 ? { missingInfo } : {}),
    };
    
    // The upper bound follows the calendar (next model year), not a fixed year that silently dropped new vehicles.
    if (year && year >= 1990 && year <= new Date().getFullYear() + 1) {
      ticket.year = year;
    }
    if (make) {
      ticket.make = normalizeMake(make);
    }
    if (model) {
      ticket.model = model;
    }
    if (engineSize) {
      ticket.engineSize = engineSize.toUpperCase();
    }
    if (customerPhone) {
      ticket.customerPhone = customerPhone;
    }
    if (assignedTechnician) {
      ticket.assignedTechnician = assignedTechnician;
    }
    if (startTime) {
      ticket.startTime = startTime;
    }
    if (vin) {
      ticket.vin = vin;
    }
    
    tickets.push(ticket);
  }
  
  return tickets;
};
